import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from "@mui/material";
import {
  VolumeUp as ClueIcon,
  Lock as LockIcon,
  LocalFireDepartment as HotSeatIcon,
  HourglassTop as WaitingIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import io from "socket.io-client";
import useGameSounds from "../../../hooks/useGameSounds";
import useHolPrepBg from "../../../hooks/useHolPrepBg";
import useHolWheelSpinSound from "../../../hooks/useHolWheelSpinSound";
import { playClueNote } from "../../../utils/audioClue";
import { getGuestDisplayName } from "../../../utils/guestDisplayUtils";
import HotSeatAnnounceModal from "../../games/HotSeatAnnounceModal";
import GameOverResultsModal from "../../games/GameOverResultsModal";
import FlippableCard from "../../games/FlippableCard";
import HolUserWheelStage from "../../games/HolUserWheelStage";
import HolGameOverScreen from "../../games/HolGameOverScreen";
import HolSessionCountdown, { useHolSessionCountdown } from "../../games/HolSessionCountdown";
import HolIntroStep from "../../AdminSide/games/hol/HolIntroStep";
import HolHowToPlayStep from "../../AdminSide/games/hol/HolHowToPlayStep";
import {
  HOL_HOT_SEAT_CLAP_URL,
  HOL_CARD_FLIP_URL,
  playHolSound,
  stopHolAudio,
} from "../../../constants/holDefaultSounds";
import {
  HOL,
  holCardSlotSx,
  holCardsBoardSx,
  holCardsGridSx,
  holClueBtnSx,
  holFadeUp,
  holHudSx,
  holPulse,
} from "./holTheme";
import { bindGameResync } from "../../../utils/bindGameResync";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const HigherOrLowerSpUser = ({ getDeviceId, getAuthToken, user, forceJoin = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joined, setJoined] = useState(false);
  const [state, setState] = useState(null);
  const [winnerModalOpen, setWinnerModalOpen] = useState(false);
  const [hotSeatAnnounce, setHotSeatAnnounce] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [clueLoading, setClueLoading] = useState(false);
  const [clueFx, setClueFx] = useState(null);
  const [finalResults, setFinalResults] = useState(null);
  const [lobbyPhase, setLobbyPhase] = useState("intro");
  const [numberOfCards, setNumberOfCards] = useState(5);
  const { countdownEndsAt, applyCountdown, clearCountdown } = useHolSessionCountdown();
  const [prepOn, setPrepOn] = useState(false);
  const socketRef = useRef(null);
  const stateRef = useRef(null);
  const autoJoinAttemptedRef = useRef(false);
  const fetchGenRef = useRef(0);
  const getDeviceIdRef = useRef(getDeviceId);
  getDeviceIdRef.current = getDeviceId;
  const clapAudioRef = useRef(null);
  const completedKeyRef = useRef(null);
  const { sounds, play, playLoop, stopBgMusic } = useGameSounds("higher_or_lower");
  const soundsRef = useRef(sounds);
  const playRef = useRef(play);
  const prevRevealedRef = useRef(null);
  const prevResultRef = useRef(null);
  stateRef.current = state;

  useEffect(() => {
    soundsRef.current = sounds;
  }, [sounds]);

  useEffect(() => {
    playRef.current = play;
  }, [play]);

  const stopWheelSpinSound = useHolWheelSpinSound({
    sounds,
    wheel: state?.user_wheel,
  });

  const playHotSeatClap = useCallback(() => {
    stopWheelSpinSound();
    playHolSound(soundsRef.current, playRef.current, "hot_seat", HOL_HOT_SEAT_CLAP_URL);
  }, [stopWheelSpinSound]);

  const playCardFlip = useCallback(() => {
    playHolSound(soundsRef.current, playRef.current, "card_flip", HOL_CARD_FLIP_URL);
  }, []);

  const stopGameOverClaps = useCallback(() => {
    stopHolAudio(clapAudioRef.current);
    clapAudioRef.current = null;
  }, []);

  const closeHotSeatAnnounce = useCallback(() => {
    setHotSeatAnnounce(null);
  }, []);

  const closeWinnerModal = useCallback(() => {
    setWinnerModalOpen(false);
    stopGameOverClaps();
  }, [stopGameOverClaps]);

  // Play flip sound whenever host reveals another card
  useEffect(() => {
    const sid = state?.session_id ?? null;
    const count = state?.revealed_count ?? 0;
    if (!sid) {
      prevRevealedRef.current = null;
      return;
    }
    if (prevRevealedRef.current === null) {
      prevRevealedRef.current = { sid, count };
      return;
    }
    if (prevRevealedRef.current.sid !== sid) {
      prevRevealedRef.current = { sid, count };
      return;
    }
    if (count > prevRevealedRef.current.count) {
      playCardFlip();
    }
    prevRevealedRef.current = { sid, count };
  }, [state?.session_id, state?.revealed_count, playCardFlip]);

  useEffect(() => {
    const sid = state?.session_id ?? null;
    const result = state?.last_result || null;
    const count = state?.revealed_count ?? 0;
    if (!sid) {
      prevResultRef.current = null;
      return;
    }
    const key = `${sid}-${count}-${result}`;
    if (prevResultRef.current === null) {
      prevResultRef.current = key;
      return;
    }
    if (prevResultRef.current === key) return;
    prevResultRef.current = key;
    if (result === "correct" || result === "tie") playRef.current("correct");
    else if (result === "wrong" || result === "incorrect") playRef.current("wrong");
  }, [state?.session_id, state?.last_result, state?.revealed_count]);

  useEffect(() => {
    const inGame = !!state?.hot_seat_display_name && !state?.game_complete;
    if (inGame) setHistoryOpen(false);
  }, [state?.hot_seat_display_name, state?.game_complete]);

  const displayName = useMemo(() => getGuestDisplayName(user), [user]);

  const fetchState = useCallback(async () => {
    const gen = ++fetchGenRef.current;
    const deviceId = getDeviceId();
    try {
      const res = await fetch(
        `${API_BASE}/api/higher-or-lower/single-player/active?device_id=${encodeURIComponent(deviceId)}`,
      );
      const data = await res.json();
      if (gen !== fetchGenRef.current) return;
      if (!data.success) throw new Error(data.error || "Failed to load");
      setPrepOn(!!data.prep_music);
      if (data.active && data.state) {
        setState(data.state);
        if (data.countdown) applyCountdown(data.countdown);
      } else {
        const oldSid = stateRef.current?.session_id;
        if (oldSid && socketRef.current?.connected) {
          socketRef.current.emit("holsp:leave-session", { sessionId: oldSid });
        }
        setState(null);
        setJoined(false);
        if (data.lobby_phase === "rules" || data.lobby_phase === "intro") {
          setLobbyPhase(data.lobby_phase);
        }
      }
      setError(null);
    } catch (e) {
      if (gen !== fetchGenRef.current) return;
      setError(e.message);
    } finally {
      if (gen === fetchGenRef.current) setLoading(false);
    }
  }, [getDeviceId, applyCountdown]);

  const fetchStateRef = useRef(fetchState);
  fetchStateRef.current = fetchState;

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  useEffect(() => {
    autoJoinAttemptedRef.current = false;
    setJoined(false);
    fetchStateRef.current();
  }, [user?.id]);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/settings/public/higher-or-lower-cards`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setNumberOfCards(parseInt(data.numberOfCards, 10) || 5);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!state?.game_complete || state?.user_wheel?.phase) {
      completedKeyRef.current = null;
      setWinnerModalOpen(false);
      stopHolAudio(clapAudioRef.current);
      clapAudioRef.current = null;
      return undefined;
    }

    const key = `${state.session_id}-${state.hot_seat_display_name}-${state.score}-${state.revealed_count}`;
    if (completedKeyRef.current === key) return undefined;

    completedKeyRef.current = key;
    setWinnerModalOpen(true);
    stopHolAudio(clapAudioRef.current);
    clapAudioRef.current = playHolSound(
      soundsRef.current,
      null,
      "hot_seat",
      HOL_HOT_SEAT_CLAP_URL,
      { loop: false, forceDirect: true },
    );

    return undefined;
  }, [
    state?.game_complete,
    state?.user_wheel?.phase,
    state?.session_id,
    state?.hot_seat_display_name,
    state?.score,
    state?.revealed_count,
  ]);

  useEffect(() => {
    return () => {
      stopHolAudio(clapAudioRef.current);
      clapAudioRef.current = null;
    };
  }, []);

  const joinQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const deviceId = getDeviceId();
      const token = getAuthToken();
      const headers = {
        "Content-Type": "application/json",
        "x-device-id": deviceId,
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/api/higher-or-lower/single-player/join`, {
        method: "POST",
        headers,
        body: JSON.stringify({ display_name: displayName }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not join");
      setJoined(true);
      setState(data.state || null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [displayName, getAuthToken, getDeviceId]);

  // Auto-join the queue as soon as we detect an active session
  useEffect(() => {
    if (joined) return;
    if (!state?.session_id) return;
    if (loading) return;
    if (autoJoinAttemptedRef.current) return;

    autoJoinAttemptedRef.current = true;
    joinQueue();
  }, [joined, state?.session_id, loading, joinQueue]);

  useEffect(() => {
    if (!state?.session_id) {
      autoJoinAttemptedRef.current = false;
    }
  }, [state?.session_id]);

  useEffect(() => {
    if (state?.session_id && finalResults && state.session_id !== finalResults.session_id) {
      setFinalResults(null);
    }
  }, [state?.session_id, finalResults]);

  const showGameOver =
    !!finalResults && (!state?.session_id || state.session_id === finalResults.session_id);

  useHolPrepBg({
    sounds,
    playLoop,
    stopBgMusic,
    prepOn,
    countdownEndsAt,
    wheelPhase: state?.user_wheel?.phase,
    suppressed: showGameOver,
  });

  useEffect(() => {
    const socket = io(API_BASE, { transports: ["websocket"] });
    socketRef.current = socket;
    const joinRoom = () => {
      const sid = stateRef.current?.session_id;
      if (sid) {
        socket.emit("holsp:join-session", { sessionId: sid, device_id: getDeviceId() });
      }
    };
    socket.on("connect", () => {
      joinRoom();
      fetchState();
    });
    const unbindResync = bindGameResync(() => {
      if (socket.connected) joinRoom();
      fetchState();
    });
    socket.on("holsp:state_updated", fetchState);
    socket.on("holsp:joiners_updated", fetchState);
    socket.on("holsp:hot_seat_selected", (payload) => {
      const deviceId = getDeviceId();
      setHotSeatAnnounce({
        display_name: payload?.display_name || "Player",
        profile_picture: payload?.profile_picture || null,
        isSelf: !!payload?.device_id && String(payload.device_id) === String(deviceId),
      });
      playHotSeatClap();
      fetchState();
    });
    socket.on("holsp:session_restarted", ({ session_id: newSessionId }) => {
      const oldSid = stateRef.current?.session_id;
      if (oldSid && oldSid !== newSessionId) {
        socket.emit("holsp:leave-session", { sessionId: oldSid });
      }
      if (newSessionId) {
        socket.emit("holsp:join-session", { sessionId: newSessionId, device_id: getDeviceId() });
      }
      // Stay in queue — server carried joiners into the new session
      setWinnerModalOpen(false);
      setHotSeatAnnounce(null);
      stopHolAudio(clapAudioRef.current);
      clapAudioRef.current = null;
      fetchState();
    });
    socket.on("holsp:session_available", () => fetchState());
    socket.on("holsp:session_countdown", (payload) => applyCountdown(payload));
    socket.on("holsp:clue_played", (payload) => {
      if (!payload?.note) return;
      if (payload.actor && String(payload.actor) === String(getDeviceId())) return;
      playClueNote(payload.note, soundsRef.current);
      setClueFx({ part: payload.part, id: Date.now() });
    });
    socket.on("holsp:lobby_phase", (payload) => {
      if (payload?.phase === "rules" || payload?.phase === "intro") setLobbyPhase(payload.phase);
    });
    socket.on("holsp:prep_music", (payload) => {
      setPrepOn(!!payload?.active);
    });
    socket.on("holsp:session_ended", (payload) => {
      setPrepOn(false);
      if (payload?.final_results) setFinalResults(payload.final_results);
      setState(null);
      setJoined(false);
      setWinnerModalOpen(false);
      setHotSeatAnnounce(null);
      stopHolAudio(clapAudioRef.current);
      clapAudioRef.current = null;
      clearCountdown();
    });

    // Heal missed broadcasts even when the socket still looks connected.
    const poll = setInterval(fetchState, 8000);

    return () => {
      unbindResync();
      clearInterval(poll);
      const sid = stateRef.current?.session_id;
      if (sid) socket.emit("holsp:leave-session", { sessionId: sid });
      socket.disconnect();
      socketRef.current = null;
    };
  }, [fetchState, playHotSeatClap, getDeviceId, applyCountdown, clearCountdown]);

  useEffect(() => {
    if (state?.session_id && socketRef.current?.connected) {
      socketRef.current.emit("holsp:join-session", {
        sessionId: state.session_id,
        device_id: getDeviceIdRef.current(),
      });
    }
  }, [state?.session_id]);

  const promptCard =
    state?.revealed_cards && state.revealed_cards.length
      ? state.revealed_cards[state.revealed_cards.length - 1]
      : null;

  const revealedCount = state?.revealed_count || 0;
  const currentCardIndex = revealedCount > 0 ? revealedCount - 1 : -1;
  const totalCards = state?.total_cards || 0;

  const clueButtonShown = state?.clue_user_can_play !== false;
  const clueUnlocked = state?.clue_unlocked === true && state?.clue_available !== false;

  useEffect(() => {
    if (!clueFx) return undefined;
    const t = setTimeout(() => setClueFx(null), 1400);
    return () => clearTimeout(t);
  }, [clueFx]);

  const canUseClue =
    joined &&
    clueUnlocked &&
    state?.is_hot_seat &&
    state?.status === "playing" &&
    !state?.game_complete &&
    !state?.pending_user_guess &&
    revealedCount >= 1 &&
    revealedCount < totalCards;

  const isClueButtonActive = (index) =>
    canUseClue &&
    !clueLoading &&
    (index === currentCardIndex || index === currentCardIndex + 1);

  const shouldShowClueButton = (index) => {
    if (!clueButtonShown || !joined || !state?.session_id || revealedCount < 1) return false;
    if (index <= currentCardIndex) return true;
    if (index === currentCardIndex + 1 && !state?.game_complete) return true;
    return false;
  };

  const handleClue = async (index) => {
    if (!canUseClue || clueLoading) return;

    let part = null;
    if (index === currentCardIndex) part = "visible";
    else if (index === currentCardIndex + 1) part = "hidden";
    else return;

    setClueLoading(true);
    setError(null);
    try {
      const deviceId = getDeviceId();
      const token = getAuthToken();
      const headers = {
        "Content-Type": "application/json",
        "x-device-id": deviceId,
      };
      if (token) headers.Authorization = `Bearer ${token}`;

      const res = await fetch(`${API_BASE}/api/higher-or-lower/single-player/clue`, {
        method: "POST",
        headers,
        body: JSON.stringify({ part, device_id: deviceId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Could not play clue");
      }
      await playClueNote(data.note, soundsRef.current);
      setClueFx({ part, id: Date.now() });
    } catch (e) {
      setError(e.message);
    } finally {
      setClueLoading(false);
    }
  };

  return (
    <Box
      sx={{
        textAlign: "center",
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        py: { xs: 0.5, sm: 1 },
        animation: `${holFadeUp} 0.45s ease both`,
      }}
    >
      {countdownEndsAt ? (
        <HolSessionCountdown endsAt={countdownEndsAt} onDone={clearCountdown} />
      ) : null}

      <HotSeatAnnounceModal
        open={!!hotSeatAnnounce}
        onClose={closeHotSeatAnnounce}
        displayName={hotSeatAnnounce?.display_name}
        profilePicture={hotSeatAnnounce?.profile_picture}
        isSelf={!!hotSeatAnnounce?.isSelf}
      />

      {/* <Typography
        sx={{
          fontFamily: HOL.fontDisplay,
          fontWeight: 800,
          fontSize: { xs: "1.05rem", sm: "1.25rem" },
          letterSpacing: "-0.02em",
          mb: 0.25,
        }}
      >
        Live Hot Seat
      </Typography>
      <Typography sx={{ color: HOL.muted, mb: 1, fontSize: "0.8rem" }}>
        Host runs the deck. You call it when you&apos;re in the chair.
      </Typography> */}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            maxWidth: 560,
            mx: "auto",
            borderRadius: "14px",
            bgcolor: "rgba(255,107,61,0.1)",
            border: `1px solid ${HOL.lower}44`,
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {showGameOver ? (
        <HolGameOverScreen
          results={finalResults}
          viewerDeviceId={getDeviceId()}
          onClose={() => setFinalResults(null)}
        />
      ) : loading && !state ? (
        <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress size={36} sx={{ color: HOL.gold }} />
          <Typography sx={{ color: HOL.muted }}>Finding the live session…</Typography>
        </Box>
      ) : !state?.session_id ? (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: { xs: 0.5, sm: 1 }, pb: 2 }}>
          {lobbyPhase === "rules" ? (
            <HolHowToPlayStep numberOfCards={numberOfCards} waiting />
          ) : (
            <HolIntroStep numberOfCards={numberOfCards} waiting />
          )}
        </Box>
      ) : !joined ? (
        <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress size={36} sx={{ color: HOL.gold }} />
          <Typography sx={{ color: HOL.muted }}>Joining the queue…</Typography>
        </Box>
      ) : state?.user_wheel?.phase ? (
        <Box sx={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <HolUserWheelStage userWheel={state.user_wheel} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              px: 2,
              py: 1,
              mb: 2,
              mx: "auto",
              width: "fit-content",
              maxWidth: "92%",
              alignSelf: "center",
              borderRadius: "999px",
              bgcolor: state.is_hot_seat ? "rgba(255,107,61,0.16)" : "rgba(255,255,255,0.05)",
              border: `1px solid ${
                state.is_hot_seat ? `${HOL.lower}66` : "rgba(255,255,255,0.1)"
              }`,
              animation: state.is_hot_seat ? `${holPulse} 2s ease-in-out infinite` : "none",
            }}
          >
            {state.is_hot_seat ? (
              <HotSeatIcon sx={{ color: HOL.lower, fontSize: 20 }} />
            ) : (
              <WaitingIcon sx={{ color: HOL.muted, fontSize: 20 }} />
            )}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.9rem",
                color: state.is_hot_seat ? HOL.lower : "#fff",
              }}
            >
              {state.is_hot_seat
                ? "You're on hot seat"
                : state.hot_seat_display_name
                  ? `Now playing: ${state.hot_seat_display_name}`
                  : "Waiting for host to select hot seat"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mb: 0.75 }}>
            <Box sx={holHudSx}>
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: HOL.muted,
                    fontWeight: 700,
                  }}
                >
                  Score
                </Typography>
                <Typography
                  sx={{
                    fontFamily: HOL.fontDisplay,
                    fontWeight: 800,
                    fontSize: "1.35rem",
                    lineHeight: 1.1,
                    color: HOL.gold,
                  }}
                >
                  {state.score ?? 0}
                  <Typography
                    component="span"
                    sx={{ color: HOL.muted, fontSize: "0.85rem", fontWeight: 600, ml: 0.5 }}
                  >
                    / {Math.max(0, (state.total_cards || 1) - 1)}
                  </Typography>
                </Typography>
              </Box>
              {state.last_result ? (
                <>
                  <Box
                    sx={{
                      width: 1,
                      alignSelf: "stretch",
                      bgcolor: "rgba(255,255,255,0.12)",
                    }}
                  />
                  <Chip
                    size="small"
                    label={
                      state.last_result === "correct"
                        ? "Correct +1"
                        : state.last_result === "tie"
                          ? "Tie +2"
                          : state.last_result === "push"
                            ? "Push"
                            : "Miss"
                    }
                    sx={{
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      bgcolor:
                        state.last_result === "correct"
                          ? "rgba(168,224,99,0.18)"
                          : state.last_result === "tie" || state.last_result === "push"
                            ? "rgba(245,215,110,0.15)"
                            : "rgba(255,107,61,0.18)",
                      color:
                        state.last_result === "correct"
                          ? HOL.higher
                          : state.last_result === "tie" || state.last_result === "push"
                            ? HOL.gold
                            : HOL.lower,
                      border: "1px solid currentColor",
                    }}
                  />
                </>
              ) : null}
            </Box>
          </Box>

          <Box sx={holCardsBoardSx}>
            <Box sx={holCardsGridSx(state.total_cards)}>
            {Array.from({ length: state.total_cards || 0 }).map((_, index) => {
              const card = state.revealed_cards?.[index];
              const revealed = !!card;
              const isCurrent = index === currentCardIndex && revealedCount >= 1 && !state.game_complete;
              const isNextHidden =
                index === currentCardIndex + 1 && !revealed && !state.game_complete;
              const isActive = isCurrent || isNextHidden;
              return (
                <Box
                  key={`${state.session_id}-${state.hot_seat_display_name || "none"}-${index}`}
                  sx={{
                    ...holCardSlotSx(state.total_cards),
                    transform: isCurrent ? "scale(1.04)" : revealed ? "scale(1)" : "scale(0.94)",
                    filter: isActive || revealedCount < 1 ? "none" : "brightness(0.72)",
                    transition: "transform 0.3s ease, filter 0.3s",
                  }}
                >
                    <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
                    <FlippableCard
                      card={card}
                      revealed={revealed}
                      maxHeight="100%"
                      minHeight={0}
                    />
                    </Box>
                    {clueFx &&
                    ((clueFx.part === "visible" && index === currentCardIndex) ||
                      (clueFx.part === "hidden" && index === currentCardIndex + 1)) ? (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "grid",
                          placeItems: "center",
                          pointerEvents: "none",
                          zIndex: 2,
                        }}
                      >
                        <Box
                          sx={{
                            width: 54,
                            height: 54,
                            borderRadius: "50%",
                            display: "grid",
                            placeItems: "center",
                            color: HOL.gold,
                            bgcolor: "rgba(0,0,0,0.55)",
                            border: `2px solid ${HOL.gold}`,
                            animation: `${holPulse} 0.7s ease-in-out infinite`,
                          }}
                        >
                          <ClueIcon />
                        </Box>
                      </Box>
                    ) : null}
                    {shouldShowClueButton(index) && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          !clueUnlocked && (index === currentCardIndex || index === currentCardIndex + 1) ? (
                            <LockIcon sx={{ fontSize: { xs: 12, sm: 16 } }} />
                          ) : (
                            <ClueIcon sx={{ fontSize: { xs: 12, sm: 16 } }} />
                          )
                        }
                        onClick={() => handleClue(index)}
                        disabled={!isClueButtonActive(index)}
                        sx={{
                          ...holClueBtnSx(isClueButtonActive(index)),
                          flexShrink: 0,
                          "& .MuiButton-startIcon": { mr: { xs: 0, sm: 0.5 } },
                          fontSize: { xs: 0, sm: "0.75rem" },
                          minWidth: { xs: 28, sm: "auto" },
                          width: { xs: 28, sm: "auto" },
                          px: { xs: 0.5, sm: 1 },
                        }}
                        aria-label={
                          !clueUnlocked && (index === currentCardIndex || index === currentCardIndex + 1)
                            ? "Clue locked"
                            : "Clue"
                        }
                      >
                        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                          {!clueUnlocked && (index === currentCardIndex || index === currentCardIndex + 1)
                            ? "Locked"
                            : "Clue"}
                        </Box>
                      </Button>
                    )}
                </Box>
              );
            })}
            </Box>
          </Box>

          <Typography
            sx={{
              mb: 1,
              flexShrink: 0,
              fontSize: { xs: "0.82rem", sm: "0.95rem" },
              color: HOL.muted,
              fontWeight: 500,
              maxWidth: 480,
              mx: "auto",
            }}
          >
            {promptCard ? (
              <>
                Next card — higher or lower than{" "}
                <Box
                  component="span"
                  sx={{
                    color: "#fff",
                    fontFamily: HOL.fontDisplay,
                    fontWeight: 800,
                  }}
                >
                  {promptCard.displayName || promptCard.name}
                </Box>
                ?
              </>
            ) : (
              "Waiting for the first flip…"
            )}
          </Typography>

          {state.pending_user_guess && (
            <Alert
              severity="info"
              icon={<HotSeatIcon sx={{ color: HOL.gold }} />}
              sx={{
                maxWidth: 520,
                mx: "auto",
                mb: 2,
                borderRadius: "14px",
                bgcolor: "rgba(245,215,110,0.1)",
                border: `1px solid ${HOL.gold}44`,
                color: "#fff",
                "& .MuiAlert-message": { width: "100%" },
              }}
            >
              Locked in:{" "}
              <Box
                component="strong"
                sx={{
                  color: state.pending_user_guess === "higher" ? HOL.higher : HOL.lower,
                  fontFamily: HOL.fontDisplay,
                  letterSpacing: "0.06em",
                }}
              >
                {state.pending_user_guess.toUpperCase()}
              </Box>
              {" — "}waiting for reveal…
            </Alert>
          )}

          <GameOverResultsModal
            open={winnerModalOpen}
            onClose={closeWinnerModal}
            score={state.score ?? 0}
            outOf={Math.max(0, (state.total_cards || 1) - 1)}
            playerLabel={state.is_hot_seat ? "Hot seat" : state.hot_seat_display_name || null}
            scoreCaption={state.is_hot_seat ? "Your score" : "Score"}
            subtitle={
              state.is_hot_seat
                ? "Host can pick the next hot seat."
                : state.hot_seat_display_name
                  ? `${state.hot_seat_display_name} finished — host can pick the next hot seat.`
                  : "Host can pick the next hot seat in the same session."
            }
          />

          {!!state.history?.length && (
            <Accordion
              expanded={historyOpen}
              onChange={(_, open) => setHistoryOpen(open)}
              disableGutters
              sx={{
                mt: 1.5,
                mb: 0.5,
                mx: "auto",
                width: "100%",
                maxWidth: 520,
                flexShrink: 0,
                borderRadius: "16px !important",
                background: HOL.panel,
                border: `1px solid ${HOL.panelBorder}`,
                color: "#fff",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: HOL.gold }} />}
                sx={{
                  minHeight: 44,
                  px: 2,
                  "& .MuiAccordionSummary-content": { my: 0.75 },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: HOL.fontDisplay,
                    fontWeight: 800,
                    fontSize: "0.85rem",
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    color: HOL.gold,
                  }}
                >
                  Hot Seat History
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  px: 2,
                  pt: 0,
                  pb: 1.5,
                  maxHeight: { xs: 140, sm: 180 },
                  overflowY: "auto",
                  textAlign: "left",
                  scrollbarWidth: "thin",
                }}
              >
                {state.history.map((h, i) => (
                  <Box
                    key={h.id}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 0.85,
                      borderBottom:
                        i < state.history.length - 1
                          ? "1px solid rgba(255,255,255,0.06)"
                          : "none",
                    }}
                  >
                    <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
                      {h.player_display_name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: HOL.fontDisplay,
                        fontWeight: 800,
                        color: HOL.higher,
                      }}
                    >
                      {h.final_score}/{h.total_rounds ?? Math.max(0, (state.total_cards || 1) - 1)}
                    </Typography>
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          )}
        </>
      )}
    </Box>
  );
};

export default HigherOrLowerSpUser;
